import React, { useCallback, useEffect, useState } from 'react';
import interpolate from 'pinterpolate';
import { RouteComponentProps } from 'react-router-dom';

import * as userService from 'services/user';

import Empty from 'components/common/empty/Empty';
import ProfileHeader from './components/profileHeader/ProfileHeader';
import Loading from 'components/common/loading/Loading';
import ProfileBody from './components/ProfileBody';

import { extractFullName } from 'utils/string';
import { handleError } from 'utils/errorHandler';
import { validateEmail } from 'utils/validEmail';

import { EMPLOYEE_PROFILE } from 'constants/routes';
import { useCurrentUser } from 'hooks/query/useUserInfo';

type RouteParams = { id?: string };

interface ProfileEmployee {
  id: number;
  firstName: string;
  middleName?: string | null;
  lastName: string;
  empStatusHistory?: Array<{ transitionDate?: string }>;
  designationAreaHistory?: Array<{ transitionDate?: string }>;
}

type EmployeeProfileProps = RouteComponentProps<RouteParams>;

const EmployeeProfile: React.FC<EmployeeProfileProps> = (props) => {
  const { match, history } = props;
  const routeEmployeeId = match.params.id;
  const [loading, setLoading] = useState<boolean>(true);
  const [employee, setEmployee] = useState<ProfileEmployee | null>(null);
  const authUser = useCurrentUser() as { id: number };

  const getPageTitle = useCallback((currentEmployee: ProfileEmployee | null) => {
    const employeeFullName = currentEmployee ? extractFullName(currentEmployee) : '';
    return employeeFullName ? `${employeeFullName}'s profile | Vyaguta` : 'Profile | Vyaguta';
  }, []);

  const updateEmployee = useCallback((data: ProfileEmployee) => {
    setEmployee({
      ...data,
      empStatusHistory: Array.isArray(data.empStatusHistory)
        ? [...data.empStatusHistory].reverse()
        : data.empStatusHistory,
      designationAreaHistory: Array.isArray(data.designationAreaHistory)
        ? [...data.designationAreaHistory].reverse()
        : data.designationAreaHistory,
    });
  }, []);

  const fetchEmployee = useCallback(
    async (userId?: string) => {
      if (!userId) {
        setLoading(false);
        setEmployee(null);
        return;
      }

      try {
        const data = (await userService.fetchById(userId)) as ProfileEmployee;
        window.document.title = getPageTitle(data);
        updateEmployee(data);
      } catch (error) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    },
    [getPageTitle, updateEmployee]
  );

  useEffect(() => {
    window.document.title = 'Profile | Vyaguta';
  }, []);

  useEffect(() => {
    const resolveProfile = async () => {
      const id = routeEmployeeId;
      const isValidEmail = validateEmail(id);

      if (isValidEmail && id) {
        try {
          const [userData] = (await userService.fetchByEmail(id)) as Array<{ id: number }>;
          if (!userData) {
            setLoading(false);
            setEmployee(null);
            return;
          }
          history.push(interpolate(EMPLOYEE_PROFILE, { id: userData.id }));
          return;
        } catch (error) {
          handleError(error);
        }
      }

      await fetchEmployee(id);
    };

    resolveProfile();
  }, [fetchEmployee, history, routeEmployeeId]);

  return (
    <div>
      {loading ? (
        <div className="container loading-container">
          <Loading />
        </div>
      ) : employee ? (
        <div>
          <div className="title profile-wrapper">
            <ProfileHeader employee={employee} user={authUser} updateEmployee={updateEmployee} />
          </div>
          <ProfileBody {...props} employee={employee} authUser={authUser} />
        </div>
      ) : (
        <main>
          <div className="container">
            <div className="full-scope-card empty-container__wrapper">
              <Empty message="Employee does not exist!" />
            </div>
          </div>
        </main>
      )}
    </div>
  );
};

export default EmployeeProfile;
